/**
 * Auth token refresh utility and terminal failure handler.
 * Manages the singleton refresh state and cleans up auth on failure.
 */
import { queryClient } from "./query-client";

import { useAuthStore } from "@/stores/auth";

/**
 * Outcome of a token-refresh attempt.
 *
 * The boolean this replaced could not express the third real-world case — a
 * refresh that failed transiently while the session is still valid — so a network
 * blip or a `5xx` was treated identically to a dead token and logged the user out.
 *
 * - `success`  — a new access token was issued (`2xx`).
 * - `terminal` — the refresh token is expired/revoked (`401`/`403`) → log out.
 * - `transient` — a recoverable failure (network error / `5xx` / `429` / other
 *   unexpected non-`2xx`) → do NOT log out; the session is still valid.
 */
export type RefreshOutcome = "success" | "terminal" | "transient";

// Bounded retry budget for transient failures: 1 initial attempt + this many
// extra attempts. Small and finite so a sustained outage cannot loop forever.
const REFRESH_MAX_RETRIES = 2;

// Backoff before each retry attempt (ms). Index 0 precedes the first retry.
const REFRESH_BACKOFF_MS = [300, 600];

// Singleton Promise to coalesce concurrent refresh attempts.
// If 5 queries fail simultaneously, only one refresh chain runs.
let refreshPromise: Promise<RefreshOutcome> | null = null;

/**
 * Sleep for the given number of milliseconds.
 * @param ms - delay in milliseconds
 * @returns a Promise that resolves after the delay
 */
function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Classify a single `POST /auth/refresh` response into a refresh outcome.
 *
 * Only `401`/`403` are terminal (the server returns `401` for an expired/revoked
 * refresh token). Every other non-`2xx` — `5xx`, `429`, and even an unexpected
 * `400`/`404` — is transient, so an ambiguous response fails safe (keep the
 * session) rather than ejecting the user.
 * @param response - the fetch Response from /auth/refresh
 * @returns the classified outcome for this attempt
 */
export function classifyResponse(response: Response): RefreshOutcome {
	if (response.ok) {
		return "success";
	}
	if (response.status === 401 || response.status === 403) {
		return "terminal";
	}
	return "transient";
}

/**
 * Attempt to refresh the access token using the refresh cookie.
 *
 * Returns a tri-state outcome so the caller can distinguish a genuinely dead
 * session (`terminal` → log out) from a recoverable blip (`transient` → keep the
 * session). Transient failures are retried a bounded number of times with backoff
 * before giving up; exhausting the budget returns `transient` (never a logout).
 *
 * Uses a shared Promise singleton so concurrent callers coalesce onto the same
 * in-flight refresh chain — the singleton wraps the ENTIRE retry lifecycle (all
 * attempts and backoffs) and is cleared only after the chain settles.
 * @returns the classified refresh outcome
 */
export async function refreshAccessToken(): Promise<RefreshOutcome> {
	// If a refresh is already in flight, return the same Promise
	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = (async () => {
		try {
			for (let attempt = 0; attempt <= REFRESH_MAX_RETRIES; attempt++) {
				let outcome: RefreshOutcome;
				try {
					// Target the configured API origin, not the web origin. In a
					// split-origin deployment (web at app.example, API at api.example) a
					// relative "/auth/refresh" hits the static web host and never reaches
					// the API; the Vite dev proxy hides this locally. Falls back to the
					// relative path when VITE_API_URL is unset (local dev via the proxy).
					const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
					const response = await fetch(`${apiUrl}/auth/refresh`, {
						method: "POST",
						credentials: "include",
					});
					outcome = classifyResponse(response);
				}
				catch {
					// A thrown fetch error (network/connection failure) is transient.
					outcome = "transient";
				}

				// success and terminal are final — return immediately.
				if (outcome !== "transient") {
					return outcome;
				}

				// Transient: back off and retry until the budget is exhausted.
				if (attempt < REFRESH_MAX_RETRIES) {
					await delay(REFRESH_BACKOFF_MS[attempt] ?? 0);
				}
			}

			// Every attempt failed transiently — give up without logging out.
			return "transient";
		}
		finally {
			refreshPromise = null;
		}
	})();

	return refreshPromise;
}

/**
 * Handle terminal auth failure (refresh token expired or invalid).
 *
 * Clears the Zustand auth store, purges the React Query cache, and
 * redirects to the login page via window.location.href to ensure a
 * full page transition and clean React state.
 */
export function handleAuthFailure(): void {
	// Clear auth store (session, user, teams, accounts, permissions)
	useAuthStore.getState().logout();

	// Purge entire React Query cache to remove stale UNAUTHORIZED errors
	queryClient.clear();

	// Full page navigation ensures React tree unmounts cleanly.
	// Using window.location.href instead of React Router navigate() so
	// stale component state, query observers, and error boundaries don't persist.
	window.location.href = "/login";
}
